/**
 * TanStack Query key factory.
 *
 * Usage:
 *   queryKey: queryKeys.projects.list(wsId)
 *   queryKey: queryKeys.targets.list(projId, { channel: 'seo' })
 */

export const queryKeys = {
  me: () => ["me"] as const,

  workspaces: {
    detail: (wsId: string) => ["workspaces", wsId] as const,
    projects: (wsId: string) => ["workspaces", wsId, "projects"] as const,
  },

  projects: {
    detail: (projId: string) => ["projects", projId] as const,
    brands: (projId: string) => ["projects", projId, "brands"] as const,
    overview: (projId: string) => ["projects", projId, "overview"] as const,
  },

  assets: {
    list: (projId: string, filters?: Record<string, unknown>) =>
      ["projects", projId, "assets", filters] as const,
    detail: (projId: string, assetId: string) =>
      ["projects", projId, "assets", assetId] as const,
  },

  targets: {
    list: (projId: string, filters?: Record<string, unknown>) =>
      ["projects", projId, "targets", filters] as const,
    detail: (projId: string, targetId: string) =>
      ["projects", projId, "targets", targetId] as const,
    observations: (projId: string, targetId: string) =>
      ["projects", projId, "targets", targetId, "observations"] as const,
  },

  insights: {
    list: (projId: string, filters?: Record<string, unknown>) =>
      ["projects", projId, "insights", filters] as const,
  },

  alerts: {
    list: (projId: string, filters?: Record<string, unknown>) =>
      ["projects", projId, "alerts", filters] as const,
    rules: (projId: string) => ["projects", projId, "alert-rules"] as const,
  },

  reports: {
    list: (projId: string) => ["projects", projId, "reports"] as const,
    detail: (projId: string, reportId: string) =>
      ["projects", projId, "reports", reportId] as const,
  },

  ai: {
    threads: (projId: string) => ["projects", projId, "ai", "threads"] as const,
    thread: (projId: string, threadId: string) =>
      ["projects", projId, "ai", "threads", threadId] as const,
  },

  research: {
    keywords: (params: Record<string, unknown>) =>
      ["research", "keywords", params] as const,
    suggestions: (keyword: string) =>
      ["research", "suggestions", keyword] as const,
    related: (keywords: string[]) =>
      ["research", "related", keywords] as const,
    apps: (params: Record<string, unknown>) =>
      ["research", "apps", params] as const,
    appCategories: (platform: string) =>
      ["research", "app-categories", platform] as const,
    topApps: (params: Record<string, unknown>) =>
      ["research", "top-apps", params] as const,
    locations: (query: string) =>
      ["research", "locations", query] as const,
    localSearch: (params: Record<string, unknown>) =>
      ["research", "local-search", params] as const,
  },
};
